import React from 'react';
import { Calendar, Package, MapPin, DollarSign, FileText, CheckCircle, Users, Trash2, X } from 'lucide-react';
import { InternalNotesButton } from './InternalNotesButton';
interface Rental {
  id: string;
  name: string;
  email: string;
  phone: string;
  eventName: string;
  customerInfo?: {
    address?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    eventVenueCityState?: string;
    preferredTechnician?: string;
  };
  proposedEventDate: string;
  alternateDate: string;
  status: 'Pending' | 'Confirmed' | 'Deposit' | 'Paid in Full';
  total: number;
  subtotal: number;
  discount: number;
  tax: number;
  added: string;
  assignedTechnician?: string;
  assignedDistributor?: string;
  invoiceId?: string;
  internalNotes?: string;
  selections: {
    gen3Body: number;
    gen3Head: number;
    gen3JudgesOnly: number;
    fullGen3Ring: number;
    requireTech: boolean;
    numberOfDays: number;
  };
}
interface RentalsManagerProps {
  rentals: Rental[];
  filter: string;
  setFilter: (filter: string) => void;
  onRentalClick: (rental: Rental) => void;
  onCreateInvoice: (rental: Rental) => void;
  onAssignTechnician: (rental: Rental) => void;
  creatingInvoice: boolean;
  hasInvoice: (rentalId: string) => boolean;
  getStatusColor: (status: string) => string;
  getDistributorColor: (distributor: string) => string;
  onNotesUpdated?: () => void;
}
export function RentalsManager({
  rentals,
  filter,
  setFilter,
  onRentalClick,
  onCreateInvoice,
  onAssignTechnician,
  creatingInvoice,
  hasInvoice,
  getStatusColor,
  getDistributorColor,
  onNotesUpdated
}: RentalsManagerProps) {
  const filteredRentals = rentals.filter(rental => {
    if (filter === 'all') return true;
    return rental.status === filter;
  });
  const groupedByMonthYear = filteredRentals.reduce((acc, rental) => {
    if (!rental.proposedEventDate) {
      if (!acc['No Date']) acc['No Date'] = [];
      acc['No Date'].push(rental);
      return acc;
    }
    const date = new Date(rental.proposedEventDate);
    const monthYear = date.toLocaleString('default', {
      month: 'long',
      year: 'numeric'
    });
    if (!acc[monthYear]) acc[monthYear] = [];
    acc[monthYear].push(rental);
    return acc;
  }, {} as Record<string, Rental[]>);
  const sortedMonthYears = Object.keys(groupedByMonthYear).sort((a, b) => {
    if (a === 'No Date') return 1;
    if (b === 'No Date') return -1;
    const dateA = new Date(groupedByMonthYear[a][0].proposedEventDate);
    const dateB = new Date(groupedByMonthYear[b][0].proposedEventDate);
    return dateA.getTime() - dateB.getTime();
  });
  return <>
      {/* Filters */}
      <div className="flex gap-2 p-6 border-b border-slate-200 overflow-x-auto" data-id="element-1425">
        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1426">
          All ({rentals.length})
        </button>
        <button onClick={() => setFilter('Pending')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Pending' ? 'bg-slate-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1427">
          Pending ({rentals.filter(r => r.status === 'Pending').length})
        </button>
        <button onClick={() => setFilter('Confirmed')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Confirmed' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1428">
          Confirmed ({rentals.filter(r => r.status === 'Confirmed').length})
        </button>
        <button onClick={() => setFilter('Deposit')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Deposit' ? 'bg-yellow-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1429">
          Deposit ({rentals.filter(r => r.status === 'Deposit').length})
        </button>
        <button onClick={() => setFilter('Paid in Full')} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${filter === 'Paid in Full' ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`} data-id="element-1430">
          Paid in Full (
          {rentals.filter(r => r.status === 'Paid in Full').length})
        </button>
      </div>

      {/* Rentals Content */}
      <div className="p-6" data-id="element-1431">
        {filteredRentals.length === 0 ? <div className="flex items-center justify-center h-64" data-id="element-1432">
            <div className="text-center" data-id="element-1433">
              <Calendar size={48} className="text-slate-300 mx-auto mb-4" data-id="element-1434" />
              <p className="text-slate-600 text-lg font-medium" data-id="element-1435">
                No rentals found
              </p>
              <p className="text-slate-500 text-sm mt-1" data-id="element-1436">
                {filter !== 'all' ? 'Try changing the filter' : 'Rentals will appear here'}
              </p>
            </div>
          </div> : <div className="space-y-8" data-id="element-1437">
            {sortedMonthYears.map(monthYear => <div key={monthYear} data-id="element-1438">
                <div className="flex items-center gap-3 mb-4" data-id="element-1439">
                  <Calendar size={20} className="text-blue-600" data-id="element-1440" />
                  <h3 className="text-lg font-semibold text-slate-800" data-id="element-1441">
                    {monthYear}
                  </h3>
                  <span className="text-sm text-slate-500" data-id="element-1442">
                    ({groupedByMonthYear[monthYear].length} rental
                    {groupedByMonthYear[monthYear].length !== 1 ? 's' : ''})
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1443">
                  {groupedByMonthYear[monthYear].map(rental => <div key={rental.id} className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-lg transition-shadow" data-id="element-1444">
                      <div className="flex items-start justify-between mb-3" data-id="element-1445">
                        <div className="flex-1 cursor-pointer" onClick={() => onRentalClick(rental)} data-id="element-1446">
                          <h4 className="font-semibold text-slate-800" data-id="element-1447">
                            {rental.name}
                          </h4>
                          <p className="text-sm text-slate-500" data-id="element-1448">
                            {rental.email}
                          </p>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-1449">
                          <InternalNotesButton rentalId={rental.id} initialNotes={rental.internalNotes} onNotesSaved={onNotesUpdated} data-id="element-1450" />
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(rental.status)}`} data-id="element-1451">
                            {rental.status}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2 mb-3 cursor-pointer" onClick={() => onRentalClick(rental)} data-id="element-1452">
                        <div className="flex items-center text-sm text-slate-600" data-id="element-1453">
                          <Package size={14} className="mr-2 text-slate-400" data-id="element-1454" />
                          <span className="font-medium" data-id="element-1455">
                            {rental.eventName}
                          </span>
                        </div>
                        {rental.proposedEventDate && <div className="flex items-center text-sm text-slate-600" data-id="element-1456">
                            <Calendar size={14} className="mr-2 text-slate-400" data-id="element-1457" />
                            <span className="font-medium text-blue-700" data-id="element-1458">
                              {(() => {
                      const startDate = new Date(rental.proposedEventDate + 'T00:00:00');
                      const startDateStr = startDate.toLocaleDateString('en-US', {
                        month: 'numeric',
                        day: 'numeric',
                        year: 'numeric'
                      });
                      if (rental.selections.numberOfDays > 1) {
                        const endDate = new Date(startDate);
                        endDate.setDate(endDate.getDate() + rental.selections.numberOfDays - 1);
                        const endDateStr = endDate.toLocaleDateString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric'
                        });
                        return `${startDateStr} - ${endDateStr}`;
                      }
                      return startDateStr;
                    })()}
                            </span>
                            <span className="ml-2 bg-purple-100 text-purple-800 text-xs font-semibold px-2 py-0.5 rounded" data-id="element-1459">
                              {rental.selections.numberOfDays}{' '}
                              {rental.selections.numberOfDays === 1 ? 'Day' : 'Days'}
                            </span>
                          </div>}
                        <div className="flex items-center text-sm text-slate-600" data-id="element-1460">
                          <MapPin size={14} className="mr-2 text-slate-400" data-id="element-1461" />
                          <span className="truncate" data-id="element-1462">
                            {[rental.customerInfo?.city, rental.customerInfo?.state].filter(Boolean).join(', ') || 'No location'}
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-slate-600" data-id="element-1463">
                          <DollarSign size={14} className="mr-2 text-slate-400" data-id="element-1464" />
                          <span className="font-semibold" data-id="element-1465">
                            ${rental.total.toFixed(2)}
                          </span>
                        </div>
                      </div>

                      <div className="pt-3 border-t border-slate-100 mb-3" data-id="element-1466">
                        <div className="flex flex-wrap gap-2 text-xs" data-id="element-1467">
                          {rental.selections.fullGen3Ring > 0 && <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded" data-id="element-1468">
                              {rental.selections.fullGen3Ring} Ring
                              {rental.selections.fullGen3Ring !== 1 ? 's' : ''}
                            </span>}
                          {rental.selections.gen3Body > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded" data-id="element-1469">
                              {rental.selections.gen3Body} Body
                            </span>}
                          {rental.selections.gen3Head > 0 && <span className="bg-slate-50 text-slate-700 px-2 py-1 rounded" data-id="element-1470">
                              {rental.selections.gen3Head} Head
                            </span>}
                          {rental.selections.requireTech && <span className="bg-red-50 text-red-700 px-2 py-1 rounded" data-id="element-1471">
                              Tech
                            </span>}
                          {rental.selections.numberOfDays > 1 && <span className="bg-purple-50 text-purple-700 px-2 py-1 rounded font-semibold" data-id="element-1472">
                              {rental.selections.numberOfDays} Days
                            </span>}
                        </div>
                        <div className="mt-2 text-xs font-semibold text-blue-700" data-id="element-1473">
                          Total Rings: {rental.selections.fullGen3Ring}
                        </div>
                        {rental.assignedTechnician && <div className="mt-2 flex items-center gap-2" data-id="element-1474">
                            <span className="text-xs text-slate-600" data-id="element-1475">
                              Tech:
                            </span>
                            <span className="text-xs font-medium text-slate-800" data-id="element-1476">
                              {rental.assignedTechnician}
                            </span>
                            {rental.assignedDistributor && <span className={`text-xs px-2 py-0.5 rounded border ${getDistributorColor(rental.assignedDistributor)}`} data-id="element-1477">
                                {rental.assignedDistributor}
                              </span>}
                          </div>}
                      </div>

                      <div className="space-y-2" data-id="element-1478">
                        {(rental.selections.requireTech || rental.selections.fullGen3Ring > 0 || rental.selections.gen3Body > 0 || rental.selections.gen3Head > 0 || rental.selections.gen3JudgesOnly > 0) && <button onClick={e => {
                  e.stopPropagation();
                  onAssignTechnician(rental);
                }} disabled={hasInvoice(rental.id)} className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium ${hasInvoice(rental.id) ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : rental.assignedTechnician ? 'bg-purple-600 text-white hover:bg-purple-700' : 'bg-orange-600 text-white hover:bg-orange-700'}`} data-id="element-1479">
                            <Users size={16} data-id="element-1480" />
                            {rental.assignedTechnician ? 'Change Technician' : 'Assign Technician'}
                          </button>}

                        <button onClick={() => onCreateInvoice(rental)} disabled={hasInvoice(rental.id) || creatingInvoice || !rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me'} className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${hasInvoice(rental.id) || creatingInvoice || !rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me' ? 'bg-slate-200 text-slate-500 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} data-id="element-1481">
                          {creatingInvoice ? <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" data-id="element-1482"></div>
                              Creating...
                            </> : hasInvoice(rental.id) ? <>
                              <CheckCircle size={18} data-id="element-1483" />
                              Invoice Created
                            </> : <>
                              <FileText size={18} data-id="element-1484" />
                              Create Invoice
                            </>}
                        </button>
                        {(!rental.assignedTechnician || rental.assignedTechnician === 'Choose For Me') && !hasInvoice(rental.id) && <p className="text-xs text-red-600 text-center" data-id="element-1485">
                              Assign a distributor to create invoice
                            </p>}
                      </div>
                    </div>)}
                </div>
              </div>)}
          </div>}
      </div>
    </>;
}